// SEROTA Yönetim girişi: telefon + 6 haneli SMS kodu. Yalnız SEROTA platform ekibi.
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { copy, formatPhone } from '@serota/i18n';
import { Alert, Button, Card, Logo, OtpInput, PhoneInput, useThemeMode } from '@serota/ui-web';
import { api, errorText } from '../lib/api';
import { NotPlatformError, useSession } from '../lib/session';

export function Login() {
  const nav = useNavigate();
  const { status, context, signIn } = useSession();
  const [, , theme] = useThemeMode();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  if (status === 'signedIn' && context) return <Navigate to="/" replace />;

  const e164 = `+90${phone}`;
  const request = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (phone.length !== 10 || !phone.startsWith('5'))
      return setError('Telefon numarası 5 ile başlayan 10 hane olmalı.');
    setBusy(true);
    try {
      const r = await api.requestOtp(e164);
      setResendAt(Date.now() + r.resendInS * 1000);
      setStep('code');
      setCode('');
    } catch (err) {
      setError(errorText(err, 'Kod gönderilemedi.'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (c = code) => {
    if (c.length !== 6 || busy) return;
    setError(null);
    setBusy(true);
    try {
      await signIn(e164, c);
      nav('/', { replace: true });
    } catch (err) {
      setError(err instanceof NotPlatformError ? err.message : errorText(err, copy.states.otpError));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const left = Math.max(0, Math.ceil((resendAt - now) / 1000));
  return (
    <div className="sr-auth">
      <Card className="sr-auth__card" pad="lg">
        <Logo height={28} variant={theme === 'dark' ? 'dark' : 'light'} />
        <h1 className="h1" style={{ marginTop: 28 }}>
          {step === 'phone' ? 'SEROTA Yönetim' : 'Kodu girin'}
        </h1>
        <p className="body sr-muted" style={{ marginTop: 6 }}>
          {step === 'phone'
            ? 'Platform ekibi hesabınıza bağlı telefon numaranızla giriş yapın.'
            : `${formatPhone(e164)} numarasına 6 haneli bir kod gönderdik.`}
        </p>
        {step === 'phone' ? (
          <form onSubmit={request} className="sr-stack" style={{ gap: 16, marginTop: 24 }}>
            <PhoneInput value={phone} onChange={setPhone} error={error ?? undefined} autoFocus />
            <Button type="submit" variant="primary" block loading={busy}>
              {copy.actions.continue}
            </Button>
          </form>
        ) : (
          <div className="sr-stack" style={{ gap: 16, marginTop: 24 }}>
            <OtpInput
              value={code}
              error={!!error}
              autoFocus
              onChange={(v) => {
                setCode(v);
                if (v.length === 6) void verify(v);
              }}
            />
            {error ? <Alert tone="crit">{error}</Alert> : null}
            <Button variant="primary" block loading={busy} disabled={code.length !== 6} onClick={() => void verify()}>
              Giriş yap
            </Button>
            <div className="sr-row" style={{ justifyContent: 'space-between' }}>
              <Button variant="ghost" size="sm" onClick={() => setStep('phone')}>
                Numarayı değiştir
              </Button>
              <Button variant="ghost" size="sm" disabled={left > 0 || busy} onClick={() => void request()}>
                {left > 0 ? `Tekrar gönder (0:${String(left).padStart(2, '0')})` : 'Kodu tekrar gönder'}
              </Button>
            </div>
          </div>
        )}
      </Card>
      <p className="caption sr-muted" style={{ textAlign: 'center' }}>
        {copy.brand.promise}
      </p>
    </div>
  );
}
